import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { CirromLoadingLogo } from "../utils/CirromLoadingLogo";

/**
 * An overlay with the cirrom logo for when data loading is in progress
 */
export function CirromGridOverlay() {
  return <CirromLoadingLogo />;
}

/**
 * Read a numeric range out of an AG Grid filter model.
 *
 * The probability slider writes a combined filter - two conditions, `>= min` AND
 * `<= max` - rather than a single `inRange`, so both shapes are handled. Returns null
 * for anything else, which falls back to naming the column.
 */
const readNumericRange = (
  model: any
): { min?: number; max?: number } | null => {
  const conditions = model?.conditions ?? (model ? [model] : []);
  const range: { min?: number; max?: number } = {};

  conditions.forEach((condition: any) => {
    if (condition?.filter === undefined) {
      return;
    }
    if (condition.type === "inRange") {
      range.min = condition.filter;
      range.max = condition.filterTo;
    } else if (/greaterThan/.test(condition.type)) {
      range.min = condition.filter;
    } else if (/lessThan/.test(condition.type)) {
      range.max = condition.filter;
    }
  });

  return range.min === undefined && range.max === undefined ? null : range;
};

/**
 * Describe the filters currently hiding rows, in the steward's terms.
 *
 * Read off the grid rather than passed in, so the list cannot drift from what is
 * actually applied. Probability is named with its actual bounds because that is the
 * filter most likely to be responsible and the one worth acting on; other columns are
 * named by their header, which is what the steward sees in the grid.
 */

const describeActiveFilters = (api): string[] => {
  const described: string[] = [];
  if (!api) {
    return described;
  }

  const filterModel = api.getFilterModel?.() || {};
  Object.entries(filterModel).forEach(([colId, model]: [string, any]) => {
    const headerName =
      api.getColumnDef?.(colId)?.headerName || colId.replace(/_/g, " ");
    const range = readNumericRange(model);

    if (colId === "probability" && range) {
      described.push(`probability ${range.min ?? 0}–${range.max ?? 100}%`);
    } else if (range) {
      described.push(
        `${headerName} ${range.min ?? "any"}–${range.max ?? "any"}`
      );
    } else {
      described.push(`a filter on ${headerName}`);
    }
  });

  const quickFilter = api.getGridOption?.("quickFilterText");
  if (quickFilter) {
    described.push(`the search "${quickFilter}"`);
  }

  return described;
};

/**
 * CirromNoRowsOverlay
 *
 * What the steward sees when the grid has nothing to show.
 *
 * This surface opens with the probability filter deliberately set to 75-100%, so an
 * empty grid is usually the filter working as intended rather than an empty queue - but
 * the two used to look identical: a blank area under a tab reading "First Name (12)",
 * with no overlay registered at all. The first read of the queue was "there is nothing
 * here", which was false.
 *
 * So the two states are told apart and worded differently:
 *
 * - Rows exist but none survive the filters: name the filters that are hiding them and
 *   offer one click to drop them. The steward gets the cause and the cure together.
 * - No rows at all: say so plainly and offer nothing, because there is nothing to undo.
 */
export function CirromNoRowsOverlay(props: {
  api?: any;
  onClearFilters?: () => void;
}) {
  const { api, onClearFilters } = props;

  // rowData is the pre-filter set; the grid only shows this overlay when the
  // post-filter count is zero, so rows here means "hidden by a filter".
  const totalRows = api?.getGridOption?.("rowData")?.length ?? 0;
  const isFilteredToZero = totalRows > 0;
  const activeFilters = isFilteredToZero ? describeActiveFilters(api) : [];

  /*
   * Pin the message just under the column headers.
   *
   * AG Grid centres its overlay across the grid's whole height, and this grid is a fixed
   * 90vh sitting below ~530px of page chrome - so a centred message rendered at y≈865 in
   * an 812px viewport. Present in the DOM, invisible to the steward, which is the same
   * failure as having no empty state at all.
   *
   *
   * Top-aligning is enough because AG Grid already pads `.ag-overlay-wrapper` by the
   * measured header height whenever it shows the no-rows overlay (floating filters and
   * group rows included), so the wrapper's content box starts below the header. What we
   * cannot do is measure the header ourselves on mount: ag-grid-react renders this
   * component into a detached container and only appends it to the grid afterwards, so
   * anything walking up to `.ag-overlay` at layout time finds nothing and leaves the
   * message sitting on top of the headers.
   *
   * The flex item is the wrapper div ag-grid-react puts around us, so the alignment has
   * to be set there rather than on this Box - reachable without being in the document.
   */
  const alignToTopOfWrapper = React.useCallback(
    (node: HTMLDivElement | null) => {
      const reactContainer = node?.parentElement;
      if (reactContainer) {
        reactContainer.style.alignSelf = "flex-start";
        reactContainer.style.width = "100%";
      }
    },
    []
  );

  return (
    <Box
      ref={alignToTopOfWrapper}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        padding: 4,
        textAlign: "center",
        /*
          AG Grid sets `pointer-events: none` on the whole overlay panel so it never
          blocks the grid underneath, and that inherits straight down to the button.
          Without this the Clear filters button rendered perfectly and swallowed nothing:
          clicks passed through it to the grid viewport behind.
        */
        pointerEvents: "auto",
        // The grid paints its own surface behind this, so only the text is coloured.
        color: "text.secondary",
      }}
    >
      <Typography variant={"body2"} sx={{ color: "text.primary" }}>
        {isFilteredToZero
          ? "No changes match the current filters"
          : "No pending changes on this tab"}
      </Typography>

      {isFilteredToZero && (
        <>
          <Typography variant={"body2"}>
            {activeFilters.length
              ? `${totalRows} ${
                  totalRows === 1 ? "change is" : "changes are"
                } hidden by ${activeFilters.join(" and ")}.`
              : `${totalRows} ${
                  totalRows === 1 ? "change is" : "changes are"
                } hidden by the current filters.`}
          </Typography>
          {onClearFilters && (
            <Button
              variant={"outlined"}
              color={"primary"}
              size={"small"}
              onClick={onClearFilters}
            >
              Clear filters
            </Button>
          )}
        </>
      )}
    </Box>
  );
}
